import {
    createContext,
    PropsWithChildren,
    useState,
    useContext,
    SetStateAction,
    useEffect
} from "react";

import {
    RoadmapFeature,
    RoadmapFeatureFilters,
    SelectedRoadmapFeature,
    SortCriteria,
    FilterOptionsByKey
} from "../types";

export const CLOUD_BASE_PATH = "/roadmap/cloud";
export const DATACENTER_BASE_PATH = "/roadmap/data-center";

export interface RoadmapFeaturesContextValue {
    pageBasePath: string;
    locale: string;
    features: RoadmapFeature[] | null;
    setFeatures: (value: RoadmapFeature[]) => void;
    filters: RoadmapFeatureFilters;
    setFilters: (value: SetStateAction<RoadmapFeatureFilters>) => void;
    filterOptionsByKey: FilterOptionsByKey;
    sortCriteria?: SortCriteria;
    setSortCriteria: (value: SortCriteria | undefined) => void;
    selectedFeature?: SelectedRoadmapFeature;
    setSelectedFeature: (value: SelectedRoadmapFeature | undefined) => void;
}

const RoadmapFeaturesContext = createContext<
    RoadmapFeaturesContextValue | undefined
>(undefined);

export const useRoadmapFeaturesContext = () => {
    const ctx = useContext(RoadmapFeaturesContext);
    if (!ctx) {
        throw new Error("missing context provider for RoadmapFeaturesContext");
    }
    return ctx;
};

export interface RoadmapFeaturesContextProviderProps extends PropsWithChildren {
    pageBasePath: string;
    locale: string;
    features: RoadmapFeature[];
    filters?: Partial<RoadmapFeatureFilters>;
    selectedFeature?: SelectedRoadmapFeature;
    sortCriteria?: SortCriteria;
    filterOptionsByKey: FilterOptionsByKey;
}

export const RoadmapFeaturesContextProvider = (
    props: RoadmapFeaturesContextProviderProps
) => {
    const { pageBasePath, locale, filterOptionsByKey } = props;

    // filters
    const initialFilters = getInitialFilters(props.filters ?? {});
    const [filters, setFilters] =
        useState<RoadmapFeatureFilters>(initialFilters);

    // sort
    const [sortCriteria, setSortCriteria] = useState<SortCriteria | undefined>(
        props.sortCriteria
    );

    // selected feature
    const [selectedFeature, setSelectedFeature] = useState<
        SelectedRoadmapFeature | undefined
    >(props.selectedFeature);

    // results
    const [features, setFeatures] = useState<RoadmapFeature[] | null>(
        props.features
    );
    useEffect(() => {
        setFeatures(props.features);
    }, [props.features]);

    return (
        <RoadmapFeaturesContext.Provider
            value={{
                pageBasePath,
                locale,
                features,
                setFeatures,
                filters,
                setFilters,
                filterOptionsByKey,
                sortCriteria,
                setSortCriteria,
                selectedFeature,
                setSelectedFeature
            }}
        >
            {props.children}
        </RoadmapFeaturesContext.Provider>
    );
};

export const emptyFilters: RoadmapFeatureFilters = {
    category: [],
    status: [],
    search: [],
    selectedProduct: []
};

function getInitialFilters(
    filters: Partial<RoadmapFeatureFilters>
): RoadmapFeatureFilters {
    return {
        ...emptyFilters,
        ...filters
    };
}
